import { TrainingZoneLegend } from '../components/TrainingZoneLegend';
import { collapseStructurePreview, stepZoneNumber } from '../utils/workoutStructure';
import { useAthleteZones } from '../hooks/useAthleteZones';
import { ActionButton, ActionIconButton, ActionToggleGroup } from '../components/ui/ActionButton';
import { AiActionButton } from '../components/AiActionButton';
import { displayDigits } from '../utils/displayDigits';
import { workoutMetricLabel } from '../utils/workoutMetricLabel';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { isRtlLocale, normalizeLocale } from '../i18n/config';
import { formatIsoDateForDisplay, isJalaliPickerLocale } from '../utils/calendarPickerLocale';
import { NmDatePickerModal } from '../components/NmDatePickerModal';
import { isoToJalaliKey, jalaliKeyToIso } from '../utils/jalali';
import { NmTabNav } from '../components/NmTabNav';
import { WorkoutStructureBar } from '../components/workout/WorkoutStructureBar';
import { WorkoutBuilder } from '../features/workout/builder/WorkoutBuilder';
import { duplicateBlockAt } from '../features/workout/builder/workoutBuilderReorder';
import { WorkoutStepConfig } from '../features/workout/builder/WorkoutStepConfig';
import { WfComparisonTable, WfStatsTable } from '../features/workout/form/WorkoutFormEditableTables';
import { WorkoutTypePickerModal } from '../features/workout/form/WorkoutTypePickerModal';
import { WorkoutAiPromptSheet, type WorkoutAiApplyPayload } from '../components/workout/WorkoutAiPromptSheet';
import { TrashIcon } from '../components/icons/TrashIcon';
import { WorkoutTypeIcon } from '../components/WorkoutTypeIcon';
import { getWorkoutTypeDisplayName } from '../utils/workoutTypeDisplay';
import {
  resolveWorkoutTitle,
  formatMinutesOnlyInput,
  formatSecondsToFormTime,
  numOrEmpty,
  parseTimeToSeconds,
} from '../features/workout/form/workoutFormHelpers';
import { WorkoutStructureSteps } from '../features/workout/WorkoutStructureSteps';
import { WsNotesCard } from '../features/workout/WorkoutSummaryComponents';
import { useAthleteMetrics } from '../hooks/useAthleteMetrics';
import { useToast, toastErrorMessage } from '../context/ToastContext';
import { forms, library as libraryApi, workouts, type WorkoutType } from '../services/api';
import { athleteSettingsApi } from '../services/athleteSettingsApi';
import {
  buildFtpPercentOptions,
  buildLthrPercentOptions,
  normalizeWorkoutBlocksTargets,
  type PrimaryIntensityMetric,
} from '../utils/intensityMetricHelpers';
import { calculatePlannedMetricsFromBlocks } from '../utils/plannedWorkoutMetrics';
import { secToHMS } from '../utils/secToHMS';
import type { ApiStructBlock } from '../utils/workoutStructure';
import {
  formatPaceFromKmh,
  kmhInputFromPaceString,
} from '../utils/paceZoneHelpers';
import {
  parseWorkoutBuilderSettings,
  type WorkoutBuilderSettings,
} from '../utils/workoutBuilderSettings';
import '../styles/workout-form.css';
import '../styles/workout-form-glass.css';
import '../styles/workout-editor-system.css';

export { formatSecondsToFormTime };

export interface WorkoutFormInitialValues {
  title?: string;
  description?: string | null;
  total_time_planned?: number | string | null;
  distance_planned?: number | string | null;
  tss_planned?: number | string | null;
  if_planned?: number | string | null;
  calories_planned?: number | string | null;
  total_time?: number | null;
  distance?: number | null;
  tss_actual?: number | string | null;
  if_actual?: number | string | null;
  calories?: number | string | null;
  preComment?: string | null;
  structure?: {
    primaryLengthMetric?: string;
    primaryIntensityMetric?: string;
    primaryIntensityTargetOrRange?: string;
    structure?: ApiStructBlock[];
  } | null;
  heart_rate_minimum?: number | null;
  heart_rate_maximum?: number | null;
  heart_rate_average?: number | null;
  power_average?: number | null;
  max_power?: number | null;
  avg_cadence?: number | null;
  max_cadence?: number | null;
  avg_speed?: number | null;
  max_speed?: number | null;
  min_max_avg?: {
    heart_rate?: { min?: number; avg?: number; max?: number };
    power?: { min?: number; avg?: number; max?: number };
    cadence?: { min?: number; avg?: number; max?: number };
    speed?: { min?: number; avg?: number; max?: number };
  };
}

/** When set, save as library template instead of calendar workout (same UI as calendar). */
export interface WorkoutFormLibraryTarget {
  libraryId: number;
  /** Present when editing an existing library exercise. */
  exerciseId?: number;
}

interface WorkoutFormModalProps {
  /** Calendar day — ignored for library saves (still used for display unless libraryTarget). */
  date: string;
  userId: number;
  onSuccess: () => void;
  onClose: () => void;
  mode?: 'create' | 'edit';
  workoutId?: number;
  initial?: WorkoutFormInitialValues;
  workoutTypeId?: number;
  workoutTypeName?: string;
  /** Library create/edit — mirrors calendar WorkoutFormModal flow. */
  libraryTarget?: WorkoutFormLibraryTarget;
}

function pickDefaultTypeId(types: WorkoutType[]): number | null {
  if (!types.length) return null;
  const bike = types.find(t => `${t.english_name} ${t.name}`.toLowerCase().includes('bike'));
  return bike?.id ?? types[0].id;
}

function ModalOverlay({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return createPortal(
    <div
      className="wf-form-overlay"
      onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {children}
    </div>,
    document.body,
  );
}

export function WorkoutFormModal({
  date: initialDate,
  userId,
  onSuccess,
  onClose,
  mode = 'create',
  workoutId,
  initial,
  workoutTypeId: propTypeId,
  workoutTypeName: propTypeName,
  libraryTarget,
}: WorkoutFormModalProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const isLibrary = libraryTarget != null;
  const isEdit = isLibrary
    ? libraryTarget.exerciseId != null
    : mode === 'edit' && workoutId != null;
  const localeCode = normalizeLocale(i18n.language);
  const dir = isRtlLocale(localeCode) ? 'rtl' : 'ltr';
  const useJalali = isJalaliPickerLocale(localeCode);
  const [date, setDate] = useState(initialDate);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const displayDate = useMemo(() => formatIsoDateForDisplay(date, localeCode), [date, localeCode]);
  const mma = initial?.min_max_avg;

  const { ftp, maxHr, ltHr, thresholdSpeedMps } = useAthleteMetrics(userId);
  const ftpValue = ftp ?? 0;
  const ltHrValue = ltHr ?? 0;
  const maxHrValue = maxHr ?? 0;
  const thresholdSpeed = thresholdSpeedMps ?? 0;

  const [tab, setTab] = useState<1 | 2>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [workoutTypeId, setWorkoutTypeId] = useState<number | null>(propTypeId ?? null);
  const [workoutTypeName, setWorkoutTypeName] = useState(propTypeName ?? '');
  const [typeModalOpen, setTypeModalOpen] = useState(() => isLibrary && !isEdit && propTypeId == null);

  const [title, setTitle] = useState(initial?.title ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [preComment, setPreComment] = useState(initial?.preComment ?? '');

  const [timePlanned, setTimePlanned] = useState(formatSecondsToFormTime(
    initial?.total_time_planned != null ? Number(initial.total_time_planned) : null,
  ));
  const [distPlanned, setDistPlanned] = useState(
    initial?.distance_planned != null ? String(Math.round(Number(initial.distance_planned) / 1000)) : '',
  );
  const [tssPlanned, setTssPlanned] = useState(numOrEmpty(initial?.tss_planned));
  const [ifPlanned, setIfPlanned] = useState(numOrEmpty(initial?.if_planned));
  const [calPlanned, setCalPlanned] = useState(numOrEmpty(initial?.calories_planned));

  const [time, setTime] = useState(formatSecondsToFormTime(initial?.total_time ?? null));
  const [dist, setDist] = useState(
    initial?.distance != null ? String(Math.round(Number(initial.distance) / 1000)) : '',
  );
  const [tss, setTss] = useState(numOrEmpty(initial?.tss_actual));
  const [ifActual, setIfActual] = useState(numOrEmpty(initial?.if_actual));
  const [calory, setCalory] = useState(numOrEmpty(initial?.calories));

  const [hrMin, setHrMin] = useState(numOrEmpty(initial?.heart_rate_minimum ?? mma?.heart_rate?.min));
  const [hrMax, setHrMax] = useState(numOrEmpty(initial?.heart_rate_maximum ?? mma?.heart_rate?.max));
  const [hrAvg, setHrAvg] = useState(numOrEmpty(initial?.heart_rate_average ?? mma?.heart_rate?.avg));
  const [powerAvg, setPowerAvg] = useState(numOrEmpty(initial?.power_average ?? mma?.power?.avg));
  const [powerMax, setPowerMax] = useState(numOrEmpty(initial?.max_power ?? mma?.power?.max));
  const [cadAvg, setCadAvg] = useState(numOrEmpty(initial?.avg_cadence ?? mma?.cadence?.avg));
  const [cadMax, setCadMax] = useState(numOrEmpty(initial?.max_cadence ?? mma?.cadence?.max));
  const [speedAvg, setSpeedAvg] = useState(numOrEmpty(initial?.avg_speed ?? mma?.speed?.avg));
  const [speedMax, setSpeedMax] = useState(numOrEmpty(initial?.max_speed ?? mma?.speed?.max));

  const [paceAvg, setPaceAvg] = useState(() => formatPaceFromKmh(initial?.avg_speed ?? mma?.speed?.avg));
  const [paceMax, setPaceMax] = useState(() => formatPaceFromKmh(initial?.max_speed ?? mma?.speed?.max));
  const paceEditingRef = useRef<'avg' | 'max' | null>(null);

  useEffect(() => {
    if (paceEditingRef.current !== 'avg') setPaceAvg(formatPaceFromKmh(speedAvg));
    if (paceEditingRef.current !== 'max') setPaceMax(formatPaceFromKmh(speedMax));
  }, [speedAvg, speedMax]);

  const commitPaceAvg = useCallback(() => {
    paceEditingRef.current = null;
    const kmh = kmhInputFromPaceString(paceAvg);
    if (!paceAvg.trim()) {
      setSpeedAvg('');
      setPaceAvg('');
      return;
    }
    if (kmh) {
      setSpeedAvg(kmh);
      setPaceAvg(formatPaceFromKmh(kmh));
    }
  }, [paceAvg]);

  const commitPaceMax = useCallback(() => {
    paceEditingRef.current = null;
    const kmh = kmhInputFromPaceString(paceMax);
    if (!paceMax.trim()) {
      setSpeedMax('');
      setPaceMax('');
      return;
    }
    if (kmh) {
      setSpeedMax(kmh);
      setPaceMax(formatPaceFromKmh(kmh));
    }
  }, [paceMax]);

  const initialSettings = parseWorkoutBuilderSettings(
    initial?.structure as Partial<WorkoutBuilderSettings> | undefined,
  );
  const workoutSettingsRef = useRef<WorkoutBuilderSettings>(initialSettings);
  const [builderSettingsToolbar, setBuilderSettingsToolbar] = useState<HTMLSpanElement | null>(null);
  const [workoutSettings, setWorkoutSettings] = useState<WorkoutBuilderSettings>(initialSettings);
  const [builderSetupDone, setBuilderSetupDone] = useState(!!initial?.structure?.primaryIntensityMetric);
  const [aiSheetOpen, setAiSheetOpen] = useState(false);

  const incomingBlocks = initial?.structure?.structure ?? [];
  const [dragData, setDragData] = useState<ApiStructBlock[]>(() =>
    incomingBlocks.length ? JSON.parse(JSON.stringify(incomingBlocks)) : [],
  );
  const [tempo, setTempo] = useState<ApiStructBlock[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [selectedLoop, setSelectedLoop] = useState<string | number | null>(null);
  const skipNextDragEffect = useRef(false);
  /** Edit mode: keep stored planned metrics until the user changes structure. */
  const allowPlannedRecalcFromStructure = useRef(!isEdit);

  const commitDragData = useCallback((value: React.SetStateAction<ApiStructBlock[]>) => {
    allowPlannedRecalcFromStructure.current = true;
    setDragData(value);
  }, []);

  const [itemModal, setItemModal] = useState(false);
  const [loopModal, setLoopModal] = useState(false);
  const [stepTypeModal, setStepTypeModal] = useState(false);
  const [durationModal, setDurationModal] = useState(false);
  const [secondaryModal, setSecondaryModal] = useState(false);
  const [intensityTarget, setIntensityTarget] = useState(false);
  const [powerMode, setPowerMode] = useState(1);
  const [itemData, setItemData] = useState({ parentIndex: 0, index: 0 });
  const { zoneSettings, loading: loadingZones, reload: fetchZones } = useAthleteZones(userId);

  const FtpArray = useMemo(() => buildFtpPercentOptions(ftpValue), [ftpValue]);
  const LthrArray = useMemo(() => buildLthrPercentOptions(ltHrValue), [ltHrValue]);

  useEffect(() => {
    if (propTypeId) return;
    workouts.getWorkoutTypes()
      .then(res => {
        const types = res.data.data?.workoutTypes ?? [];
        const id = pickDefaultTypeId(types);
        if (id != null) {
          setWorkoutTypeId(id);
          const found = types.find(x => x.id === id);
          if (found) setWorkoutTypeName(getWorkoutTypeDisplayName(found, t));
        }
      })
      .catch(() => {});
  }, [propTypeId]);

  useEffect(() => {
    if (!incomingBlocks.length) return;
    const normalized = normalizeWorkoutBlocksTargets(
      incomingBlocks,
      workoutSettings.primaryIntensityMetric as PrimaryIntensityMetric,
      { ftp: ftpValue, ltHr: ltHrValue, maxHr: maxHrValue, thresholdSpeedMps: thresholdSpeed },
      workoutSettings.primaryIntensityTargetOrRange,
    ) as ApiStructBlock[];
    skipNextDragEffect.current = true;
    setDragData(normalized);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial structure load only
  }, []);

  useEffect(() => {
    if (skipNextDragEffect.current) {
      skipNextDragEffect.current = false;
      return;
    }
    if (!dragData.length || !allowPlannedRecalcFromStructure.current) return;

    const metrics = calculatePlannedMetricsFromBlocks(dragData, {
      primaryIntensityMetric: workoutSettingsRef.current.primaryIntensityMetric,
      primaryIntensityTargetOrRange: workoutSettingsRef.current.primaryIntensityTargetOrRange,
      ftp: ftpValue,
      ltHr: ltHrValue,
      maxHr: maxHrValue,
      thresholdSpeedMps: thresholdSpeed,
    });

    if (metrics.total_time_planned > 0) {
      setTimePlanned(secToHMS(metrics.total_time_planned));
    }
    if (metrics.distance_planned != null && metrics.distance_planned > 0) {
      setDistPlanned(String(Math.round(metrics.distance_planned / 1000)));
    }
    if (metrics.tss_planned != null) setTssPlanned(String(metrics.tss_planned));
    if (metrics.if_planned != null) setIfPlanned(String(metrics.if_planned));
  }, [dragData, ftpValue, ltHrValue, maxHrValue, thresholdSpeed, workoutSettings.primaryIntensityMetric, workoutSettings.primaryIntensityTargetOrRange]);

  const handleWorkoutSettingsChange = useCallback((s: WorkoutBuilderSettings) => {
    allowPlannedRecalcFromStructure.current = true;
    workoutSettingsRef.current = s;
    setWorkoutSettings(s);
  }, []);

  const handleAiApply = useCallback((payload: WorkoutAiApplyPayload) => {
    const suggestedType = payload.suggested_workout_type;
    if (suggestedType?.id) {
      setWorkoutTypeId(suggestedType.id);
      setWorkoutTypeName(suggestedType.persian_title || suggestedType.title);
    }
    const struct = payload.structure;
    const nextSettings = parseWorkoutBuilderSettings(struct as Partial<WorkoutBuilderSettings>);
    workoutSettingsRef.current = nextSettings;
    setWorkoutSettings(nextSettings);
    setBuilderSetupDone(true);
    const blocks = struct.structure ?? [];
    if (blocks.length) {
      allowPlannedRecalcFromStructure.current = true;
      setDragData(JSON.parse(JSON.stringify(blocks)));
    }
    if (payload.suggested_title?.trim()) {
      setTitle(payload.suggested_title.trim());
    }
    if (payload.warnings?.length) {
      toast.info(payload.warnings.join(' · '));
    }
  }, [toast]);

  const normalizeStructureBlocks = useCallback(
    (blocks: ApiStructBlock[]) =>
      normalizeWorkoutBlocksTargets(
        blocks,
        workoutSettingsRef.current.primaryIntensityMetric as PrimaryIntensityMetric,
        { ftp: ftpValue, ltHr: ltHrValue, maxHr: maxHrValue, thresholdSpeedMps: thresholdSpeed },
        workoutSettingsRef.current.primaryIntensityTargetOrRange,
      ),
    [ftpValue, ltHrValue, maxHrValue, thresholdSpeed],
  );

  const buildStructurePayload = useCallback(
    (blocks: ApiStructBlock[]) => ({
      primaryLengthMetric: workoutSettingsRef.current.primaryLengthMetric,
      primaryIntensityMetric: workoutSettingsRef.current.primaryIntensityMetric,
      primaryIntensityTargetOrRange: workoutSettingsRef.current.primaryIntensityTargetOrRange,
      structure: normalizeStructureBlocks(blocks),
    }),
    [normalizeStructureBlocks],
  );

  const handleBlurTimePlanned = useCallback(() => {
    const formatted = formatMinutesOnlyInput(timePlanned);
    if (formatted) setTimePlanned(formatted);
  }, [timePlanned]);

  const heroDuration = time || timePlanned;
  const heroDistance = dist || distPlanned;
  const heroTss = tss || tssPlanned;

  async function handleSave() {
    const savedTitle = resolveWorkoutTitle(title, workoutTypeName, t('workoutForm.pageTitle'));
    if (!workoutTypeId) {
      setError(t('workoutForm.error.typeRequired'));
      return;
    }
    setError('');
    setSubmitting(true);

    try {
      const structure = dragData.length ? buildStructurePayload(dragData) : undefined;
      const structureToPersist = structure && (!isEdit || allowPlannedRecalcFromStructure.current)
        ? structure
        : undefined;
      const structureMetrics = structure && tab === 2
        ? calculatePlannedMetricsFromBlocks(dragData, {
            primaryIntensityMetric: workoutSettingsRef.current.primaryIntensityMetric,
            primaryIntensityTargetOrRange: workoutSettingsRef.current.primaryIntensityTargetOrRange,
            ftp: ftpValue,
            ltHr: ltHrValue,
            maxHr: maxHrValue,
            thresholdSpeedMps: thresholdSpeed,
          })
        : null;
      const plannedTimeSeconds = structureMetrics?.total_time_planned
        ? structureMetrics.total_time_planned
        : timePlanned ? parseTimeToSeconds(timePlanned) : null;
      const plannedDistanceMeters = structureMetrics?.distance_planned
        ? structureMetrics.distance_planned
        : distPlanned ? Number(distPlanned) * 1000 : null;
      const plannedTss = structureMetrics?.tss_planned ?? (tssPlanned ? Number(tssPlanned) : null);
      const plannedIf = structureMetrics?.if_planned ?? (ifPlanned ? Number(ifPlanned) : null);

      if (isLibrary && libraryTarget) {
        const exercisePayload = {
          title: savedTitle,
          workout_type_id: workoutTypeId,
          description: description.trim() || null,
          distance_planned: plannedDistanceMeters,
          total_time_planned: plannedTimeSeconds,
          tss_planned: plannedTss,
          if_planned: plannedIf,
          calories_planned: calPlanned ? Number(calPlanned) : null,
          ...(structureToPersist ? { structure: structureToPersist } : {}),
        };

        if (isEdit && libraryTarget.exerciseId != null) {
          await libraryApi.updateExercise(libraryTarget.exerciseId, {
            data: { exercise: exercisePayload },
          });
          toast.success(t('toast.updated'));
        } else {
          await libraryApi.addExercise(libraryTarget.libraryId, exercisePayload);
          toast.success(t('toast.created'));
        }

        onSuccess();
        return;
      }

      const workoutPayload: Record<string, unknown> = {
        title: savedTitle,
        workout_type_id: workoutTypeId,
        workout_day: `${date} 00:00:00`,
        description: description.trim() || null,
        distance_planned: plannedDistanceMeters,
        total_time_planned: plannedTimeSeconds,
        tss_planned: plannedTss,
        if_planned: plannedIf,
        calories_planned: calPlanned ? Number(calPlanned) : null,
        power_average: powerAvg ? Number(powerAvg) : null,
        heart_rate_minimum: hrMin ? Number(hrMin) : null,
        heart_rate_maximum: hrMax ? Number(hrMax) : null,
        heart_rate_average: hrAvg ? Number(hrAvg) : null,
        distance: dist ? Number(dist) * 1000 : null,
        total_time: time ? parseTimeToSeconds(time) : null,
        calories: calory ? Number(calory) : null,
        tss_actual: tss ? Number(tss) : null,
        if_actual: ifActual ? Number(ifActual) : null,
        max_cadence: cadMax ? Number(cadMax) : null,
        avg_cadence: cadAvg ? Number(cadAvg) : null,
        max_power: powerMax ? Number(powerMax) : null,
        avg_power: powerAvg ? Number(powerAvg) : null,
        avg_speed: speedAvg ? Number(speedAvg) : null,
        max_speed: speedMax ? Number(speedMax) : null,
      };

      if (structureToPersist) {
        workoutPayload.structure = structureToPersist;
      }

      const comments = preComment.trim()
        ? [{
            workout_id: isEdit ? workoutId : null,
            comment_type: 1,
            description: preComment.trim(),
          }]
        : null;

      if (isEdit && workoutId) {
        await workouts.updateWorkout(workoutId, workoutPayload, comments);
        toast.success(t('toast.updated'));
      } else {
        await forms.createWorkout(userId, {
          ...workoutPayload,
          workout_type_id: workoutTypeId,
          workout_day: `${date} 00:00:00`,
        }, comments);
        toast.success(t('toast.created'));
      }

      onSuccess();
    } catch (e: unknown) {
      const msg = isEdit ? t('workoutForm.error.updateFailed') : t('workoutForm.error.createFailed');
      setError(msg);
      toast.error(toastErrorMessage(e, msg));
    } finally {
      setSubmitting(false);
    }
  }

  const structurePreview = dragData.length
    ? {
        primaryLengthMetric: workoutSettings.primaryLengthMetric,
        primaryIntensityMetric: workoutSettings.primaryIntensityMetric,
        structure: dragData,
      }
    : initial?.structure ?? null;

  return (
    <ModalOverlay onClose={onClose}>
    <div className="wf-form wf-form--embedded" dir={dir}>
      <header className="wf-form__header">
        <ActionIconButton
          type="button"
          className="wf-form__back"
          onClick={onClose}
          label={t('common.close')}
          icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false"><path d="m6 6 12 12M18 6 6 18" /></svg>}
        />
        <h1 className="wf-form__title">
          {isLibrary
            ? (isEdit ? t('library.editWorkout') : t('workoutForm.pageTitle'))
            : (isEdit ? t('workoutForm.pageTitleEdit') : t('workoutForm.pageTitle'))}
        </h1>
        <ActionButton
          type="button"
          className="wf-form__save"
          disabled={submitting}
          onClick={() => void handleSave()}
          variant="primary"
        >{submitting
            ? t('workoutForm.action.saving')
            : isEdit
              ? t('workoutForm.action.saveChanges')
              : t('workoutForm.action.save')}</ActionButton>
      </header>

      <div className="wf-form__tabs-wrap">
        <NmTabNav ariaLabel={t('workoutDetail.tabs.label')}
          className="wf-form__tabs-segment"
          value={tab}
          onChange={setTab}
          items={[
            { value: 1, label: t('workoutForm.tab.summary') },
            { value: 2, label: t('workoutForm.tab.builder') },
          ]}
        />
      </div>

      <div className="wf-form__body">
        {tab === 1 ? (
          <div className="wf-form__summary">
            <div className={`wf-form__date-chip${useJalali && !isLibrary ? ' wf-form__date-chip--jalali' : ''}`}>
              {!isLibrary && (
                <ActionButton
                  type="button"
                  className="wf-form__date wf-form__date-button"
                  onClick={() => setDatePickerOpen(true)}
                >{displayDate}</ActionButton>
              )}
              <ActionButton
                type="button"
                className="wf-form__type-btn"
                onClick={() => setTypeModalOpen(true)}
              >{workoutTypeId != null ? (
                  <WorkoutTypeIcon typeId={workoutTypeId} name={workoutTypeName} size={14} />
                ) : null}
                <span>{workoutTypeName || t('workoutForm.pickWorkoutType')}</span></ActionButton>
            </div>

            <div className="wf-form__name-field">
              <label htmlFor="wf-workout-title">{t('workoutForm.field.optionalTitle')}</label>
              <input
                id="wf-workout-title"
                className="wf-form__title-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t('workoutForm.field.titlePlaceholder')}
              />
            </div>

            <div className="wf-form__hero wf-form__hero--profile">
              <div className="wf-form__hero-metrics">
                <div><strong dir="ltr">{displayDigits(heroDuration ? secToHMS(parseTimeToSeconds(heroDuration) ?? 0) : '—', i18n.language)}</strong><span>{t('workoutForm.metrics.time')}</span></div>
                {heroDistance && <div><strong dir="ltr">{displayDigits(heroDistance, i18n.language)} <small>km</small></strong><span>{t('workoutForm.metrics.distance')}</span></div>}
                <div><strong dir="ltr">{displayDigits(heroTss || '—', i18n.language)}</strong><span>{workoutMetricLabel('TSS')}</span></div>
              </div>
              {structurePreview?.structure?.length ? (
                <div className="wf-form__profile">
                  <button type="button" className="wf-form__profile-edit" onClick={() => setTab(2)}>
                    <span>{t('workoutDetail.summary.structureTitle')}</span>
                    <span>{t('workoutDetail.summary.structureTapEdit')}</span>
                  </button>
                  <div className="wf-form__profile-plot">
                    <WorkoutStructureBar
                      structure={structurePreview.structure}
                      primaryIntensityMetric={workoutSettings.primaryIntensityMetric}
                      ftpValue={ftpValue}
                      ltHrValue={ltHrValue}
                      containerWidthPercent={100}
                      barHeightPx={72}
                      maxHeightPx={72}
                    />
                  </div>
                  <TrainingZoneLegend compact zones={collapseStructurePreview(structurePreview.structure).map(step => ({
                    number: stepZoneNumber(step, { primaryIntensityMetric: workoutSettings.primaryIntensityMetric, ftp: ftpValue, ltHr: ltHrValue }),
                  }))} />
                </div>
              ) : null}
            </div>

            <WfComparisonTable
              plannedOnly={!isEdit}
              rows={[
                {
                  rowKey: 'time',
                  planned: timePlanned,
                  done: time,
                  onChangePlanned: setTimePlanned,
                  onBlurPlanned: handleBlurTimePlanned,
                  onChangeDone: setTime,
                  placeholder: '00:00:00',
                  inputMode: 'text',
                },
                {
                  rowKey: 'distance',
                  planned: distPlanned,
                  done: dist,
                  onChangePlanned: setDistPlanned,
                  onChangeDone: setDist,
                },
                {
                  rowKey: 'tss',
                  planned: tssPlanned,
                  done: tss,
                  onChangePlanned: setTssPlanned,
                  onChangeDone: setTss,
                },
                {
                  rowKey: 'if',
                  planned: ifPlanned,
                  done: ifActual,
                  onChangePlanned: setIfPlanned,
                  onChangeDone: setIfActual,
                  inputMode: 'decimal',
                },
                {
                  rowKey: 'calories',
                  planned: calPlanned,
                  done: calory,
                  onChangePlanned: setCalPlanned,
                  onChangeDone: setCalory,
                },
              ]}
            />

            {isEdit && <WfStatsTable
              showPace={workoutTypeId !== 2 && workoutTypeId !== 8}
              rows={[
                {
                  rowKey: 'hr',
                  caption: t('workoutForm.stats.hrCaption'),
                  label: 'bpm',
                  min: hrMin,
                  avg: hrAvg,
                  max: hrMax,
                  onChangeMin: setHrMin,
                  onChangeAvg: setHrAvg,
                  onChangeMax: setHrMax,
                },
                {
                  rowKey: 'power',
                  caption: t('workoutForm.stats.powerCaption'),
                  label: 'W',
                  avg: powerAvg,
                  max: powerMax,
                  onChangeAvg: setPowerAvg,
                  onChangeMax: setPowerMax,
                },
                {
                  rowKey: 'cadence',
                  caption: t(workoutTypeId === 1 ? 'runningMetrics.cadence' : 'workoutForm.stats.cadenceCaption'),
                  label: workoutTypeId === 1 ? t('runningMetrics.cadenceUnit') : 'rpm',
                  avg: cadAvg,
                  max: cadMax,
                  onChangeAvg: setCadAvg,
                  onChangeMax: setCadMax,
                },
                {
                  rowKey: 'speed',
                  caption: t('workoutForm.stats.speedCaption'),
                  label: 'km/h',
                  avg: speedAvg,
                  max: speedMax,
                  onChangeAvg: setSpeedAvg,
                  onChangeMax: setSpeedMax,
                  inputMode: 'decimal',
                },
                {
                  rowKey: 'pace',
                  caption: t('workoutForm.stats.paceCaption'),
                  label: t('workoutForm.stats.paceUnit'),
                  avg: paceAvg,
                  max: paceMax,
                  onChangeAvg: setPaceAvg,
                  onChangeMax: setPaceMax,
                  onFocusAvg: () => { paceEditingRef.current = 'avg'; },
                  onFocusMax: () => { paceEditingRef.current = 'max'; },
                  onBlurAvg: commitPaceAvg,
                  onBlurMax: commitPaceMax,
                  inputMode: 'text',
                  placeholder: t('workoutForm.stats.pacePlaceholder'),
                  rowClassName: 'ws-table__row--pace',
                },
              ]}
            />}

            <WsNotesCard
              label={t('workoutForm.field.description')}
              value={description}
              onChange={setDescription}
              placeholder={t('workoutForm.field.descriptionPlaceholder')}
            />



            {structurePreview?.structure?.length ? (
              <WorkoutStructureSteps
                structure={structurePreview.structure as Parameters<typeof WorkoutStructureSteps>[0]['structure']}
                primaryLengthMetric={workoutSettings.primaryLengthMetric}
                primaryIntensityMetric={workoutSettings.primaryIntensityMetric}
                intensityThresholds={{ ftp, ltHr, maxHr, thresholdSpeedMps }}
              />
            ) : null}

            {error && <p className="wf-form__error">{error}</p>}
          </div>
        ) : (
          <div className="wf-form__builder">
            <div className="wf-form__builder-title-row">
              <div className="wf-form__name-field">
                <label htmlFor="wf-workout-title">{t('workoutForm.field.optionalTitle')}</label>
              <input
                  id="wf-workout-title"
                className="wf-form__title-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={t('workoutForm.field.titlePlaceholder')}
              />
              </div>
              <div className="wf-form__builder-actions">
              <span ref={setBuilderSettingsToolbar} className="wf-form__builder-settings" />
              {builderSetupDone && (
                <AiActionButton className="wf-form__ai-btn"
                  onClick={() => setAiSheetOpen(true)}
                  title={t('workoutBuilder.ai.buildWithAi')}
                >
                  {t('workoutBuilder.ai.buildWithAi')}</AiActionButton>
              )}
              {dragData.length > 0 && (
                <ActionIconButton
                  type="button"
                  className="wf-form__clear-structure"
                  onClick={() => { commitDragData([]); setTempo([]); }}
                  title={t('workoutBuilder.clearStructure')}
                  variant="danger"
                  label={t('workoutBuilder.clearStructure')}
                  icon={<TrashIcon size={16} />}
                />
              )}
              </div>
            </div>

            <WorkoutBuilder
              settingsToolbar={builderSettingsToolbar}
              data={dragData}
              onItemPress={(index, parentIndex) => {
                setTempo(dragData);
                if (parentIndex != null) {
                  setItemData({ parentIndex, index });
                } else {
                  setItemData({ parentIndex: index, index: 0 });
                }
                setItemModal(true);
              }}
              onLoopPress={index => {
                setTempo(dragData);
                setSelectedLoop(index);
                setLoopModal(true);
              }}
              onDragEnd={(repeat, newData) => {
                if (repeat !== null) {
                  const a = [...dragData];
                  a[repeat] = { ...a[repeat], steps: newData };
                  setDragActive(false);
                  commitDragData([...a]);
                } else {
                  commitDragData(newData);
                  setDragActive(false);
                }
              }}
              dragActive={dragActive}
              onDragStart={() => setDragActive(true)}
              selectedLoop={selectedLoop}
              loopPress={index => setSelectedLoop(index === selectedLoop ? null : index)}
              Delete={(repeat, _item, index) => {
                if (repeat !== null) {
                  const a = [...dragData];
                  const b = a[repeat].steps?.filter((_, i) => i !== index);
                  a[repeat] = { ...a[repeat], steps: b };
                  commitDragData(a);
                } else {
                  commitDragData(dragData.filter((_, i) => i !== index));
                }
              }}
              Duplicate={index => {
                commitDragData(prev => duplicateBlockAt(prev, index));
              }}
              onSettingsChange={handleWorkoutSettingsChange}
              settingsConfirmed={builderSetupDone}
              onSettingsConfirmed={() => setBuilderSetupDone(true)}
              onSetupCancel={onClose}
              initialSettings={builderSetupDone ? workoutSettings : undefined}
              ftpValue={ftpValue}
              ltHrValue={ltHrValue}
              maxHrValue={maxHrValue}
              thresholdSpeedMps={thresholdSpeed}
            />

            <WorkoutStepConfig
              itemModal={itemModal}
              setItemModal={setItemModal}
              loopModal={loopModal}
              setLoopModal={setLoopModal}
              stepTypeModal={stepTypeModal}
              setStepTypeModal={setStepTypeModal}
              durationModal={durationModal}
              setDurationModal={setDurationModal}
              secondaryModal={secondaryModal}
              setSecondaryModal={setSecondaryModal}
              intensityTarget={intensityTarget}
              setIntensityTarget={setIntensityTarget}
              dragData={dragData}
              setDragData={commitDragData}
              tempo={tempo}
              setTempo={setTempo}
              itemData={itemData}
              powerMode={powerMode}
              setPowerMode={setPowerMode}
              loadingZones={loadingZones}
              zoneSettings={zoneSettings}
              fetchZones={fetchZones}
              ftpValue={ftpValue}
              FtpArray={FtpArray}
              ltHrValue={ltHrValue}
              LthrArray={LthrArray}
              maxHrValue={maxHrValue}
              thresholdSpeedMps={thresholdSpeed}
              selectedLoop={selectedLoop}
              primaryIntensityMetric={workoutSettings.primaryIntensityMetric}
              primaryIntensityTargetOrRange={workoutSettings.primaryIntensityTargetOrRange}
            />

            {error && <p className="wf-form__error">{error}</p>}
          </div>
        )}
      </div>

      <NmDatePickerModal
        open={datePickerOpen}
        value={isoToJalaliKey(date)}
        onClose={() => setDatePickerOpen(false)}
        onConfirm={value => { setDate(jalaliKeyToIso(value)); setDatePickerOpen(false); }}
        zIndex={15000}
      />

      <WorkoutTypePickerModal
        open={typeModalOpen}
        selectedId={workoutTypeId}
        onSelect={item => {
          setWorkoutTypeId(item.id);
          setWorkoutTypeName(getWorkoutTypeDisplayName(item, t));
        }}
        onClose={() => setTypeModalOpen(false)}
      />

      <WorkoutAiPromptSheet
        open={aiSheetOpen}
        onClose={() => setAiSheetOpen(false)}
        userId={userId}
        workoutTypeId={workoutTypeId}
        workoutSettings={workoutSettings}
        currentStructure={{ ...workoutSettings, structure: dragData }}
        onApply={handleAiApply}
      />
    </div>
    </ModalOverlay>
  );
}
